import type { SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert } from "react-bootstrap";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

import useUserStore from "../stores/User";

import supabase from "../utils/Supabase";
import { validateEmail } from "../utils/UserUtil";
import { showToast } from "../utils/Toasts";

import logo from "../img/sh-logo_site.png";

const HIDE_REGISTRATION = process.env.REACT_APP_HIDE_REGISTRATION === 'true';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

function Register() {
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [diagnosticCode, setDiagnosticCode] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");

  const [showLoginDetails, setShowLoginDetails] = useState(true);

  const navigate = useNavigate();
  const userData = useUserStore((state) => state.data);

  const [disposableDomains, setDisposableDomains] = useState<Set<string>>(new Set());

  async function getDisposableDomains(): Promise<Set<string>> {
    const res = await fetch(
      'https://raw.githubusercontent.com/disposable-email-domains/disposable-email-domains/main/disposable_email_blocklist.conf'
    );
    const text = await res.text();
    const disposableDomains = new Set(text.split('\n').map(l => l.trim()).filter(Boolean));
    disposableDomains.add('edu.pl');
    disposableDomains.add('duck.com');
    disposableDomains.add('quiet-branch.com');
    disposableDomains.add('radiant-flow.org');
    disposableDomains.add('embercomet.com');
    disposableDomains.add('orbitxtrue.com');
    disposableDomains.add('mailcosmic.com');
    disposableDomains.add('orionswift.com');
    disposableDomains.add('cipheriron.com');
    disposableDomains.add('astrorapid.com');
    disposableDomains.add('betascalar.com');
    disposableDomains.add('neonsender.com');
    disposableDomains.add('axiscobalt.com');
    disposableDomains.add('stellarnano.org');
    disposableDomains.add('planetfast.net');
    disposableDomains.add('polarninja.com');
    disposableDomains.add('pilotrapid.com');
    disposableDomains.add('loopphoton.com');
    disposableDomains.add('tensordelta.net');
    disposableDomains.add('polarastro.com');
    disposableDomains.add('nebulasnap.com');
    disposableDomains.add('warpzen.net');
    disposableDomains.add('cipherneon.com');
    disposableDomains.add('sourcepi.org');
    disposableDomains.delete(''); // Remove any empty strings
    return disposableDomains;
  }

  const isBlockedDomain = (domain: string, blockedDomains: Set<string>): boolean => {
    if (blockedDomains.has(domain)) return true; // exact match
    return Array.from(blockedDomains).some(blocked => domain.endsWith('.' + blocked)); // subdomain match
  };

  useEffect(() => {
    if (disposableDomains.size === 0) {
      getDisposableDomains().then(domains => {
        setDisposableDomains(domains);
      }).catch(err => {
        console.error('Failed to fetch disposable email domains:', err);
      });
    }
  }, []);

  useEffect(() => {
    if (userData && userData.user_id) {
      navigate("/database");
    }
  }, [userData, navigate]);

  useEffect(() => {
    const emailDomain = email.split('@')[1]?.toLowerCase().trim();
    if (email.trim().length === 0 && emailConfirm.trim().length === 0) {
      setEmailErrorMessage("");
    } else if (!validateEmail(email)) {
      setEmailErrorMessage("Please enter a valid email address.");
    } else if (isBlockedDomain(emailDomain, disposableDomains)) {
      setEmailErrorMessage("Disposable email addresses are not allowed.");
    } else if (email !== emailConfirm && emailConfirm.trim().length > 0) {
      setEmailErrorMessage("Email addresses do not match.");
    } else {
      setEmailErrorMessage("");
    }
  }, [email, emailConfirm]);

  useEffect(() => {
    if (password.trim().length === 0 && passwordConfirm.trim().length === 0) {
      setPasswordErrorMessage("");
    } else if (password.length < 10) {
      setPasswordErrorMessage("Your password isn't long enough.");
    } else if (password !== passwordConfirm && passwordConfirm.trim().length > 0) {
      setPasswordErrorMessage("Passwords do not match.");
    } else {
      setPasswordErrorMessage("");
    }
  }, [password, passwordConfirm]);

  async function register(event: SyntheticEvent) {
    event.preventDefault();
    setShowLoginDetails(false);
    setErrorMessage("");
    setDiagnosticCode("");

    const { error } = await supabase.auth.signUp({
      email: email,
      password: password,
      options: {
        emailRedirectTo: `${window.location.origin}/database`,
        captchaToken: captchaToken ?? undefined,
      },
    });

    if (error) {
      console.error("Registration error:", error);
      setErrorMessage("Something went wrong, please try again.");
      setDiagnosticCode(`${error.status ?? '???'}: ${error.message}`);
      setShowLoginDetails(true);
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      return;
    }
  }

  return (
    <>
      <main className="d-flex" style={{ flex: "max-content" }}>
        <div className="m-auto">
          <img
            className="p-10 m-8 hivelogo"
            src={logo}
            alt="Script Hive logo"
            width={90}
          ></img>

          { HIDE_REGISTRATION ? (
            <div className="mt-3">
              <p className="text-center">
                Sorry, new user registration is temporarily closed due to an increase in scraping and malicious activity.<br/>
                Please check back soon!
              </p>
            </div>
          ) : (
            <div className="mt-3">
              {showLoginDetails ? (
                <form className="form mx-4" onSubmit={register}>
                  <p className="text-justified">
                    Register here for access to <strong>Script Hive</strong>.
                  </p>

                  {errorMessage && (
                    <Alert
                      key="danger"
                      variant="danger"
                      className="mt-3"
                    >
                      {errorMessage}
                      {diagnosticCode && (
                        <div className="mt-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(diagnosticCode);
                              showToast("info", "Diagnostic code copied to clipboard.");
                            }}
                          >
                            Copy diagnostic code
                          </Button>
                          <Form.Text className="d-block mt-1">
                            If this keeps happening, click the button above and share the copied code with support.
                          </Form.Text>
                        </div>
                      )}
                    </Alert>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      aria-label="Enter your email"
                      disabled={disposableDomains.size === 0} // Disable until domains are loaded
                      onChange={(e) => {
                          setErrorMessage("");
                          setEmail(e.target.value);
                        }
                      }
                    />
                    <Form.Control
                      type="email"
                      placeholder="Confirm your email"
                      aria-label="Confirm your email"
                      disabled={disposableDomains.size === 0} // Disable until domains are loaded
                      onChange={(e) => {
                          setErrorMessage("");
                          setEmailConfirm(e.target.value);
                        }
                      }
                    />
                    <Form.Text className="text-muted">
                      By accessing this free resource, you agree to receive periodic emails from <strong>Script Hive</strong>. Unsubscribe at any time.
                    </Form.Text>
                    {emailErrorMessage && (
                      <Alert key="danger" variant="danger" className="mt-3">
                        {emailErrorMessage}
                      </Alert>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      aria-label="Enter your password"
                      onChange={(e) => {
                          setErrorMessage("");
                          setPassword(e.target.value);
                        }
                      }
                    />
                    <Form.Control
                      type="password"
                      placeholder="Confirm your password"
                      aria-label="Confirm your password"
                      onChange={(e) => {
                          setErrorMessage("");
                          setPasswordConfirm(e.target.value);
                        }
                      }
                    />
                    <Form.Text className="text-muted">
                      Passwords must be <strong>at least 10 characters long</strong>.
                    </Form.Text>
                    {passwordErrorMessage && (
                      <Alert key="danger" variant="danger" className="mt-3">
                        {passwordErrorMessage}
                      </Alert>
                    )}
                  </Form.Group>

                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => {
                      setCaptchaToken(null);
                      turnstileRef.current?.reset();
                    }}
                    className="mt-3"
                  />

                  <Button
                    className="form-control mt-3"
                    type="submit"
                    variant="primary"
                    disabled={
                      email.trim().length === 0 ||
                      password.trim().length === 0 ||
                      errorMessage.length > 0 ||
                      emailErrorMessage.length > 0 ||
                      passwordErrorMessage.length > 0 ||
                      captchaToken === null
                    }
                  >
                    Request access
                  </Button>
                </form>
              ) : (
                <Alert key="info" variant="info">
                    Check your email to complete registration.<br/>
                    Please note that the link expires after <strong>30 minutes</strong>.<br/>
                    If you don't receive an email, please check your spam folder or try registering again.
                </Alert>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default Register;
